import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import React from "react";
import { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import { useAppSelector } from "@/app/lib/hooks";

interface Props extends ReactDatePickerCustomHeaderProps {
  // Total number of months rendered side-by-side. When the picker
  // is in single-month mode (mobile / narrow viewports) we need BOTH
  // arrows on the only header; in two-month mode the prev arrow lives
  // on the first header and the next arrow on the second.
  monthsShown?: number;
}

const DatePickerCustomHeaderTwoMonth = ({
  monthDate,
  customHeaderCount,
  decreaseMonth,
  increaseMonth,
  monthsShown = 2,
}: Props) => {
  const hidePrev = customHeaderCount > 0;
  const hideNext = customHeaderCount < monthsShown - 1;

  // The "June 2026" / "July 2026" label was hardcoded to en-US so the
  // header stayed in English even when the site was rendering in
  // French / Italian / etc. Honour the user's selected language by
  // pulling it from Redux and feeding it (with locale tag form, e.g.
  // `fr-FR`) to Intl. Falls back to en-US when the slice is empty.
  const selectedLanguage = useAppSelector(
    (s: any) => s?.language?.selectedLanguage
  );
  const localeTag = selectedLanguage
    ? String(selectedLanguage).replace("_", "-")
    : "en-US";

  return (
    <div>
      <button
        aria-label="Previous Month"
        className={
          "react-datepicker__navigation react-datepicker__navigation--previous absolute -top-1 left-0 flex items-center justify-center p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
        }
        style={hidePrev ? { visibility: "hidden" } : {}}
        onClick={decreaseMonth}
        type="button"
      >
        <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">
          <ChevronLeftIcon className="w-5 h-5" />
        </span>
      </button>
      <span
        className="react-datepicker__current-month"
        style={{ textTransform: "capitalize" }}
      >
        {monthDate.toLocaleString(localeTag, {
          month: "long",
          year: "numeric",
        })}
      </span>
      <button
        aria-label="Next Month"
        className="react-datepicker__navigation react-datepicker__navigation--next absolute -top-1 -right-0 flex items-center justify-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
        style={hideNext ? { visibility: "hidden" } : {}}
        type="button"
        onClick={increaseMonth}
      >
        <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">
          <ChevronRightIcon className="w-5 h-5" />
        </span>
      </button>
    </div>
  );
};

export default DatePickerCustomHeaderTwoMonth;
